/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { throttleUntilChanged } from '../../utils/rxjs-utils';
import * as actionType from '../../store/state/actions';
import { ActionManagerInstance } from '../../actions/manager';
import * as actions from '../../store/state/actions/action-creators';
import * as StateManager from '../../store/state/state-manager';
import { getResolvedTargetName } from '../../router/routing-manager';
import { getSemanticURL } from '../../router/app-router';
import { isErrorActionType } from '../../case/common-helper';
import { processDataToDispatchErrorAction, processViewResponse, getActionPayload } from '../../store/state/epics/util';
import constants, { publicConstants } from '../../constants';
import { clearMessages } from '../../messages';
import { getParentContext } from '../../utils/context-utils';
import type { Action } from '../../store/state/state-manager';
import { onErrorApplyErrorStateAndMergeButtons } from '../../utils/case-utils';

import executeSubmitDataObjectFlowAction from './async-request';

type Options = {
  resourceType: string;
  key: string;
  containerItemID: string;
};
const addAdditionalActions = (
  actionsOut: Action[],
  commonInfo: object,
  response: { data: object },
  options: Options
) => {
  const { resourceType, key, containerItemID } = options;
  const payload = getActionPayload(response, commonInfo);
  payload.resourceType = resourceType || publicConstants.RESOURCE_TYPES.DATA;
  const parentContext = getParentContext(containerItemID);
  actionsOut.push(actions.postProcess(actions.removeContainerItem, payload));
  const dataContext = StateManager.getValue('.dataContext', 'dataInfo', parentContext);
  const dataContextParameters = StateManager.getValue('.dataContextParameters', 'dataInfo', parentContext);
  const nextActionOptions = {
    context: parentContext,
    skipSemanticUrl: true,
    resourceType,
    parentAction: actionType.SUBMIT_DATA_OBJECT_ACTION,
    dataContext,
    dataContextParameters
  };
  const generatedKey =
    (dataContext &&
      dataContextParameters &&
      getSemanticURL(
        actionType.ACTION_SHOWDATA,
        {
          pageName: constants.PY_DETAILS,
          dataViewName: dataContext
        },
        dataContextParameters
      )) ||
    '';
  if (containerItemID?.includes(constants.PREVIEW)) {
    // trigger pyDataPreview
    const pyDataPreview = {
      type: actionType.SHOW_DATA,
      payload: {
        pageName: constants.VIEW_NAMES.PY_DATA_PREVIEW,
        container: constants.PREVIEW,
        containerName: constants.PREVIEW,
        ...nextActionOptions
      }
    };
    actionsOut.push(pyDataPreview);
  } else if (resourceType !== constants.PAGE && key === generatedKey && containerItemID?.includes(constants.PRIMARY)) {
    const pyDetails = {
      type: actionType.SHOW_DATA,
      payload: {
        pageName: constants.VIEW_NAMES.PY_DETAILS,
        containerName: '',
        ...nextActionOptions
      }
    };
    actionsOut.push(pyDetails);
  }
  return actionsOut;
};

const submitDataObjectFlowAction = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: Action[] = [];
  const throttleValue = 100;

  return action$.pipe(
    ofType(actionType.SUBMIT_DATA_OBJECT_ACTION),
    throttleUntilChanged(throttleValue),
    switchMap((action: any) => {
      const { isFormValid } = action.payload;
      ({ actionMgrID } = action.payload);
      const context = action.payload.context;
      if (!isFormValid) {
        const error = { type: publicConstants.SUBMISSION_FAILURE_TYPE.FORM_VALIDATION_ERROR };
        ActionManagerInstance.rejectActionByID(actionMgrID, true, error);
        // return empty observable as form is not valid.
        return of([]);
      }
      clearMessages({
        context,
        stateObj: StateManager.getDataObject(context),
        clearContextMessages: true
      });
      return of(action.payload).pipe(
        mergeMap((actionPayload) => {
          return executeSubmitDataObjectFlowAction(actionPayload);
        }),
        mergeMap(async (data: any) => {
          if (data && data.type === actionType.ERROR) {
            onErrorApplyErrorStateAndMergeButtons(data, 'dataObject');
            const { payload } = data;
            const errorAction = processDataToDispatchErrorAction({ ...data, ...payload, actionMgrID });
            actionsOut.push(errorAction);
            return actionsOut;
          }
          const { actionInPayload, response, headers = {} } = data;
          const { containerName, containerItemID, resourceType, key } = actionInPayload;
          const target = getResolvedTargetName(containerName, actionInPayload.context);
          const options = { acName: target, context: actionInPayload.context };
          await processViewResponse(response, actionInPayload, options);
          const commonInfo = {
            key,
            target,
            resourceType,
            containerItemID,
            headers,
            context: actionInPayload.context
          };
          actionsOut = addAdditionalActions(actionsOut, commonInfo, response, { key, resourceType, containerItemID });
          return actionsOut;
        }),
        map(() => {
          if (actionsOut.length === 1 && isErrorActionType(actionsOut[0].type)) {
            ActionManagerInstance.rejectActionByID(actionMgrID);
          } else {
            ActionManagerInstance.resolveActionByID(actionMgrID);
          }
          StateManager.dispatch(actionsOut);
          actionsOut = [];
          return actionsOut;
        })
      );
    })
  );
};

export { submitDataObjectFlowAction };
